import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from "@mui/material/TablePagination";
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import MainContainer from '../../components/MainContainer';
import { headerDataAssignment } from '../../data/modules/assignment-header';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Title from '../../components/Title';
import Spacer from '../../components/Spacer';
import DirectionService from "../../services/DirectionService";
import PlanningService from "../../services/PlanningService";
import SearchIcon from '@mui/icons-material/Search';
import LoadingSimpleBackdrop from "../tools/LoadingSimpleBackdrop";
import UserNotactifService from "../../services/UserNotactifService";
import CircularProgress from "@mui/material/CircularProgress";
import TableViewIcon from '@mui/icons-material/TableView';
import * as XLSX from 'xlsx';
import HistoryIcon from '@mui/icons-material/History';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button
} from "@material-ui/core";
import UserService from "../../services/UserService";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(1),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    elevation: 0
  },
  dialogPaper: {
    minWidth: 800,
    maxWidth: 900,
  },
}));

function HistoryDialog({ open, onClose, userId, userName }) {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (open && userId) {
      loadHistory();
    } else {
      setHistory([]);
    }
  }, [open, userId]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const result = await UserNotactifService.detailperson(userId);
      if (result && result.data) {
        setHistory(result.data);
      } else {
        setHistory([]);
      }
    } catch (error) {
      console.error("Erreur lors du chargement de l'historique:", error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: useStyles().dialogPaper }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Historique - {userName}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Direction</strong></TableCell>
                  <TableCell><strong>Service</strong></TableCell>
                  <TableCell><strong>Fonction</strong></TableCell>
                  <TableCell><strong>Financement</strong></TableCell>
                  <TableCell><strong>Date Début</strong></TableCell>
                  <TableCell><strong>Date Fin</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary" style={{ padding: '20px' }}>
                        Aucun historique disponible
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  history.map((h, index) => (
                    <TableRow key={index}>
                      <TableCell>{h.direction}</TableCell>
                      <TableCell>{h.service}</TableCell>
                      <TableCell>{h.fonction}</TableCell>
                      <TableCell>{h.funding_code}</TableCell>
                      <TableCell>{h.date_debut}</TableCell>
                      <TableCell>{h.date_fin}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

HistoryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.number,
  userName: PropTypes.string,
};

function Row(props) {
  const { row, onViewHistory } = props;

  const handleViewHistory = () => {
    onViewHistory(row.userid, row.name);
  };

  return (
    <TableRow>
      <TableCell>
        <IconButton
          aria-label="view history"
          size="small"
          onClick={handleViewHistory}
          color="primary"
        >
          <DescriptionIcon/> 
        </IconButton>
      </TableCell>
      <TableCell component="th" scope="row">{row.name}</TableCell>
      <TableCell align="center">{row.fonction}</TableCell>
      <TableCell align="center">{row.matricule}</TableCell>
      <TableCell align="center">{row.telephone}</TableCell>
      <TableCell align="center">{row.email}</TableCell>
      <TableCell align="center">{row.funding}</TableCell>
    </TableRow>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    userid: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    fonction: PropTypes.string.isRequired,
    telephone: PropTypes.string.isRequired,
    matricule: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    funding: PropTypes.string.isRequired,
  }).isRequired,
  onViewHistory: PropTypes.func.isRequired,
};

function createData(userid, name, fonction, matricule, telephone, email, funding) {
  return {
    userid,
    name,
    fonction,
    matricule,
    telephone,
    email,
    funding,
  };
}

export default function AssignmentTable() {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState({ id: null, name: '' });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const [direction, setDirection] = useState([]);
  const [service, setService] = useState([]);
  const [form, setForm] = useState({
    direction_id: localStorage.getItem("USER_DIR"),
    service_id: 0
  });
  const [iddir, setIddir] = useState(0);
  const [idservice, setIdservice] = useState(0);
  const [filters, setFilters] = useState({
    direction_id: localStorage.getItem("USER_DIR"),
    service_id: 0
  });

  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);

  function handleChangeDirection(event) {
    const { name, value } = event.target;
    setForm({ ...form, [name]: value });
    setIddir(value);
  }

  function handleChangeService(ev) {
    const { name, value } = ev.target;
    setForm({ ...form, [name]: value });
    setIdservice(value);
  }

  const handleSubmit = () => {
    setFilters({
      direction_id: form.direction_id,
      service_id: form.service_id
    });
  };

  const handleViewHistory = (userId, userName) => {
    setSelectedUser({ id: userId, name: userName });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser({ id: null, name: '' });
  };

  useEffect(() => {
    setIddir(localStorage.getItem("USER_DIR"));
    DirectionService.getDirection().then((res) => {
      if (res && res.status) {
        setDirection(res.data);
      }
    });
  }, []);

  useEffect(() => {
    if (iddir !== 0 && iddir !== "") {
      PlanningService.Direction(iddir).then((results) => {
        if (results && results.data) {
          setService(results.data);
        }
      });
    }
  }, [iddir]);

  function loadAllUser() {
    setOpenLoadingSimpleBackdrop(true);
    UserService.getAllUserlist(filters.direction_id, filters.service_id).then((result) => {
      if (result && result.data) {
        setRows(
          result.data.map((row) =>
            createData(
              row.user_id,
              row.full_name,
              row.function_name,
              row.registration_number,
              row.telephone,
              row.email,
              row.funding
            )
          )
        );
        setOpenLoadingSimpleBackdrop(false);
      } else {
        setRows([]);
        setOpenLoadingSimpleBackdrop(false);
      }
    });
  }

  useEffect(() => {
    loadAllUser();
  }, [filters.direction_id, filters.service_id]);

  const handleExportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(rows.map(row => ({
      Nom: row.name,
      Fonction: row.fonction,
      Matricule: row.matricule,
      Téléphone: row.telephone,
      Email: row.email,
      Financement: row.funding,
    })));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Liste Utilisateurs");
    XLSX.writeFile(wb, "utilisateurs.xlsx");
  };

  return (
    <MainContainer {...headerDataAssignment}>
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />
      
      <HistoryDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        userId={selectedUser.id}
        userName={selectedUser.name}
      />
      
      <div style={{ display: 'flex', gap: '16px' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          startIcon={<SearchIcon />}
          style={{ fontWeight: 800, width: 170 }}
        >
          Filtrer
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          onClick={handleExportToExcel}
          style={{ fontWeight: 600, width: "30%", display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <TableViewIcon style={{ marginRight: '8px' }} />
          Export excel
        </Button>
      </div>
      
      <Spacer y={4} />
      
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={5} sm={6} lg={5}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="direction-label">Direction</InputLabel>
            <Select
              labelId="direction-label"
              label="Direction"
              value={form.direction_id}
              name="direction_id"
              onChange={handleChangeDirection}
            >
              <MenuItem value={0}>Toutes</MenuItem>
              {direction.map((row) => (
                <MenuItem key={row.direction_id} value={row.direction_id}>
                  {row.code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={5} sm={6} lg={5}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="service-label">Service</InputLabel>
            <Select
              labelId="service-label"
              label="Service"
              value={form.service_id}
              name="service_id"
              onChange={handleChangeService}
            >
              <MenuItem value={0}>Tous</MenuItem>
              {service.map((row) => (
                <MenuItem key={row.service_id} value={row.service_id}>
                  {row.code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper className={classes.paper} variant="outlined">
            <Title style={{ textAlign: "center" }}>Liste des utilisateurs</Title>
            <Spacer y={2} />
            
            <TableContainer component={Paper}>
              <Table aria-label="utilisateurs table">
                <TableHead>
                  <TableRow>
                    <TableCell>Actions</TableCell>
                    <TableCell>Nom et prénom(s)</TableCell>
                    <TableCell align="center">Fonction</TableCell>
                    <TableCell align="center">Matricule</TableCell>
                    <TableCell align="center">Téléphone</TableCell>
                    <TableCell align="center">Email</TableCell>
                    <TableCell align="center">Financement</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((row, index) => (
                    <Row 
                      key={row.userid || index} 
                      row={row} 
                      onViewHistory={handleViewHistory}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Lignes par page"
            />
          </Paper>
        </Grid>
      </Grid>
    </MainContainer>
  );
}